import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * Reusable multi-select state keyed by stable row ids.
 *
 * @param {Array<Record<string, unknown>>} rows
 * @param {(row: Record<string, unknown>, index: number) => string} getRowKey
 * @param {(row: Record<string, unknown>) => boolean} [isSelectable]
 */
export function useRowSelection(rows, getRowKey, isSelectable = () => true) {
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const getRowKeyRef = useRef(getRowKey);
  const isSelectableRef = useRef(isSelectable);
  getRowKeyRef.current = getRowKey;
  isSelectableRef.current = isSelectable;

  const selectableKeys = useMemo(() => {
    const keys = [];
    (rows || []).forEach((row, index) => {
      if (isSelectableRef.current(row)) {
        keys.push(String(getRowKeyRef.current(row, index)));
      }
    });
    return keys;
  }, [rows]);

  // Drop selections that are no longer on the page / no longer selectable.
  useEffect(() => {
    setSelectedKeys((prev) => {
      if (prev.size === 0) return prev;
      const allowed = new Set(selectableKeys);
      let changed = false;
      const next = new Set();
      prev.forEach((key) => {
        if (allowed.has(key)) next.add(key);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [selectableKeys]);

  const clearSelection = useCallback(() => {
    setSelectedKeys(new Set());
  }, []);

  const toggleRow = useCallback((key, checked) => {
    const k = String(key);
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) next.add(k);
      else next.delete(k);
      return next;
    });
  }, []);

  const toggleAllSelectable = useCallback(
    (checked) => {
      if (!checked) {
        setSelectedKeys(new Set());
        return;
      }
      setSelectedKeys(new Set(selectableKeys));
    },
    [selectableKeys],
  );

  const selectedRows = useMemo(() => {
    const set = selectedKeys;
    return (rows || []).filter((row, index) =>
      set.has(String(getRowKeyRef.current(row, index))),
    );
  }, [rows, selectedKeys]);

  const allSelectableSelected =
    selectableKeys.length > 0 &&
    selectableKeys.every((k) => selectedKeys.has(k));

  const someSelectableSelected =
    selectableKeys.some((k) => selectedKeys.has(k)) && !allSelectableSelected;

  return {
    selectedKeys,
    selectedRows,
    selectedCount: selectedKeys.size,
    selectableCount: selectableKeys.length,
    allSelectableSelected,
    someSelectableSelected,
    toggleRow,
    toggleAllSelectable,
    clearSelection,
    isSelected: (key) => selectedKeys.has(String(key)),
  };
}
